import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  AllTrackingThunk,
  DeleteTrackingThunk,
} from "../../redux/states/tracking/thunk";
import NonAuthLayout from "../../Layout/NonAuthLayout";
import {
  Eye,
  Edit,
  Trash2,
  Search,
  RefreshCw,
  AlertCircle,
  Calendar,
  Truck,
  Package,
  Plus,
  MapPin,
  Clock,
  CheckCircle,
  PackageCheck,
  TruckIcon,
} from "lucide-react";
import ConfirmDeleteModal from "../../Modals/ConfirmDeleteModal";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

const ManageOrderTracking = () => {
  const dispatch = useDispatch();
  const { trackings, loading, error } = useSelector((state) => state.tracking);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredTrackings, setFilteredTrackings] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const navigate = useNavigate();

  // Fetch all tracking data
  useEffect(() => {
    dispatch(AllTrackingThunk());
  }, [dispatch]);

  // Filter data by search term and status
  useEffect(() => {
    let filtered = trackings?.filter(
      (t) =>
        t.tracking_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.courier_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.current_status?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.current_location?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (statusFilter !== "all") {
      filtered = filtered?.filter((t) => t.current_status === statusFilter);
    }

    setFilteredTrackings(filtered || []);
  }, [trackings, searchTerm, statusFilter]);

  const handleDeleteClick = (uuid) => {
    setSelectedId(uuid);
    setModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (selectedId) {
      dispatch(DeleteTrackingThunk(selectedId))
        .unwrap()
        .then(() => {
          toast.success("Tracking deleted successfully!");
          dispatch(AllTrackingThunk());
        })
        .catch(() => {
          toast.error("Failed to delete tracking");
        });
      setModalOpen(false);
      setSelectedId(null);
    }
  };

  const handleView = (tracking_number) => {
    navigate(`/view-order-tracking/${tracking_number}`);
  };

  const handleEdit = (uuid) => {
    navigate(`/update-new-order-tracking/${uuid}`);
  };

  const handleAdd = () => {
    navigate(`/add-new-order-tracking`);
  };

  const handleRefresh = () => {
    dispatch(AllTrackingThunk());
    toast.success("Data refreshed!");
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "delivered":
        return <CheckCircle className="w-4 h-4" />;
      case "in-transit":
        return <TruckIcon className="w-4 h-4" />;
      case "pending":
        return <Clock className="w-4 h-4" />;
      default:
        return <Package className="w-4 h-4" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "delivered":
        return "bg-green-500";
      case "in-transit":
        return "bg-blue-500";
      case "pending":
        return "bg-yellow-500";
      case "cancelled":
        return "bg-red-500";
      default:
        return "bg-gray-500";
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case "delivered":
        return "bg-green-50 text-green-700 border-green-200";
      case "in-transit":
        return "bg-blue-50 text-blue-700 border-blue-200";
      case "pending":
        return "bg-yellow-50 text-yellow-700 border-yellow-200";
      case "cancelled":
        return "bg-red-50 text-red-700 border-red-200";
      default:
        return "bg-gray-50 text-gray-700 border-gray-200";
    }
  };

  const statusCounts = {
    all: trackings?.length || 0,
    delivered:
      trackings?.filter((t) => t.current_status === "delivered").length || 0,
    "in-transit":
      trackings?.filter((t) => t.current_status === "in-transit").length || 0,
    pending:
      trackings?.filter((t) => t.current_status === "pending").length || 0,
  };

  return (
    <NonAuthLayout>
      <div className="min-h-screen p-2">
        {/* Header Section */}
        <div className="mb-4">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-2">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-600 rounded-2xl shadow-lg">
                <Truck className="w-8 h-8 text-white" />
              </div>
              <div>
                <h3 className="text-3xl font-bold text-gray-800 bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                  Order Tracking
                </h3>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleAdd}
                className="flex items-center gap-3 px-6 py-3.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
              >
                <Plus className="w-5 h-5" />
                <span className="font-semibold">Add Tracking</span>
              </button>
              <button
                onClick={handleRefresh}
                disabled={loading}
                className="flex items-center gap-3 px-6 py-3.5 bg-white border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-all duration-300 shadow-sm hover:shadow-md"
              >
                <RefreshCw
                  className={`w-5 h-5 ${loading ? "animate-spin" : ""}`}
                />
                <span className="font-medium">Refresh</span>
              </button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-1 mb-3">
          {/* Total Trackings */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">
                  Total Trackings
                </p>
                <p className="text-3xl font-bold text-gray-800 mt-2">
                  {statusCounts?.all}
                </p>
              </div>
              <div className="p-3 bg-blue-100 rounded-xl">
                <Package className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>

          {/* Delivered */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Delivered</p>
                <p className="text-3xl font-bold text-gray-800 mt-2">
                  {statusCounts?.delivered}
                </p>
              </div>
              <div className="p-3 bg-green-100 rounded-xl">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>

          {/* In Transit */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">In Transit</p>
                <p className="text-3xl font-bold text-gray-800 mt-2">
                  {statusCounts["in-transit"]}
                </p>
              </div>
              <div className="p-3 bg-blue-100 rounded-xl">
                <TruckIcon className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>

          {/* Pending */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Pending</p>
                <p className="text-3xl font-bold text-gray-800 mt-2">
                  {statusCounts?.pending}
                </p>
              </div>
              <div className="p-3 bg-yellow-100 rounded-xl">
                <Clock className="w-6 h-6 text-yellow-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filter Section */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 mb-4">
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Search */}
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search Trackings
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="text-gray-400 w-5 h-5" />
                </div>
                <input
                  type="text"
                  className="w-full pl-10 pr-4 py-3.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 bg-gray-50 hover:bg-white"
                  placeholder="Search by tracking number, courier, location, or status..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            {/* Status Filter */}
            <div className="lg:w-64">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filter by Status
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-4 py-3.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 bg-gray-50 hover:bg-white"
              >
                <option value="all">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="in-transit">In Transit</option>
                <option value="delivered">Delivered</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>
        </div>

        {/* Tracking List */}
        <div className="space-y-4">
          {loading ? (
            // Loading State
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-12 text-center">
              <RefreshCw className="w-12 h-12 text-blue-500 animate-spin mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-800 mb-2">
                Loading Trackings
              </h3>
              <p className="text-gray-600">
                Please wait while we fetch your tracking data...
              </p>
            </div>
          ) : filteredTrackings?.length > 0 ? (
            // Tracking Cards
            filteredTrackings?.map((track, index) => (
              <div
                key={track.uuid}
                className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-all duration-300 hover:border-blue-200"
              >
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  {/* Left Section - Tracking Info */}
                  <div className="flex-1">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-4">
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-3 h-3 rounded-full ${getStatusColor(
                            track.current_status
                          )}`}
                        ></div>
                        <h3 className="text-lg font-bold text-gray-800">
                          #{track.tracking_number || "N/A"}
                        </h3>
                      </div>
                      <div className="flex items-center gap-6">
                        <span
                          className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border text-sm font-medium capitalize ${getStatusBadgeColor(
                            track.current_status
                          )}`}
                        >
                          {getStatusIcon(track.current_status)}
                          {track.current_status || "N/A"}
                        </span>
                        <span className="text-sm text-gray-500">
                          Updated{" "}
                          {track.created_at
                            ? new Date(track.created_at).toLocaleDateString()
                            : "N/A"}
                        </span>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      <div className="flex items-center gap-3">
                        <Truck className="w-4 h-4 text-gray-400" />
                        <div>
                          <p className="text-sm text-gray-600">Courier</p>
                          <p className="font-medium text-gray-800">
                            {track.courier_name || "N/A"}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        <MapPin className="w-4 h-4 text-gray-400" />
                        <div>
                          <p className="text-sm text-gray-600">
                            Current Location
                          </p>
                          <p className="font-medium text-gray-800">
                            {track.current_location || "N/A"}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        <div>
                          <p className="text-sm text-gray-600">Created</p>
                          <p className="font-medium text-gray-800">
                            {track.created_at
                              ? new Date(track.created_at).toLocaleString(
                                  "en-IN",
                                  {
                                    day: "2-digit",
                                    month: "short",
                                    year: "numeric",
                                  }
                                )
                              : "N/A"}
                          </p>
                        </div>
                      </div>
                    </div>

                    {track.remarks && (
                      <div className="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">Remarks:</span>{" "}
                          {track.remarks}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Right Section - Actions */}
                  <div className="flex items-center gap-2 lg:flex-col lg:gap-3">
                    <button
                      onClick={() => handleView(track.tracking_number)}
                      className="flex items-center gap-2 px-4 py-2.5 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-xl transition-all duration-200 group"
                      title="View Details"
                    >
                      <Eye className="w-4 h-4 group-hover:scale-110 transition-transform" />
                      <span className="font-medium">View</span>
                    </button>

                    <button
                      onClick={() => handleEdit(track.tracking_number)}
                      className="flex items-center gap-2 px-4 py-2.5 bg-yellow-50 text-yellow-600 hover:bg-yellow-100 rounded-xl transition-all duration-200 group"
                      title="Edit Tracking"
                    >
                      <Edit className="w-4 h-4 group-hover:scale-110 transition-transform" />
                      <span className="font-medium">Edit</span>
                    </button>

                    <button
                      onClick={() => handleDeleteClick(track.uuid)}
                      className="flex items-center gap-2 px-4 py-2.5 bg-red-50 text-red-600 hover:bg-red-100 rounded-xl transition-all duration-200 group"
                      title="Delete Tracking"
                    >
                      <Trash2 className="w-4 h-4 group-hover:scale-110 transition-transform" />
                      <span className="font-medium">Delete</span>
                    </button>
                  </div>
                </div>
              </div>
            ))
          ) : (
            // Empty State
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-12 text-center">
              <div className="max-w-md mx-auto">
                <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-800 mb-2">
                  No Tracking Data Found
                </h3>
                <p className="text-gray-600 mb-6">
                  {searchTerm || statusFilter !== "all"
                    ? "Try adjusting your search or filter criteria."
                    : "Get started by adding your first tracking record."}
                </p>
                {searchTerm || statusFilter !== "all" ? (
                  <button
                    onClick={() => {
                      setSearchTerm("");
                      setStatusFilter("all");
                    }}
                    className="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all duration-300"
                  >
                    Clear Filters
                  </button>
                ) : (
                  <button
                    onClick={handleAdd}
                    className="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all duration-300 flex items-center gap-2 mx-auto"
                  >
                    <Plus className="w-5 h-5" />
                    Add First Tracking
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Delete Modal */}
        <ConfirmDeleteModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          onConfirm={handleConfirmDelete}
          title="Delete Tracking Record"
          message="Are you sure you want to delete this tracking record? This action cannot be undone and will permanently remove all tracking data."
        />
      </div>
    </NonAuthLayout>
  );
};

export default ManageOrderTracking;
